// ./src/utils/api/EventPartnerPackageService.ts
//
// EventPartnerPackage — an event's OFFERING of a catalog partner package: which of the account's
// packages this event actually offers, at what price, how many slots, and whether it is taking
// applications. The catalog itself is PartnerPackageService (account-wide, reusable); this is the
// per-event decision about it. A confirmed partner is EventPartnerService.
//
// ⚠ ONE OFFERING PER PACKAGE PER EVENT — the database carries a soft-delete-safe
// UNIQUE (event_id, partner_package_id, removed_at_str). So the UI is a PICK LIST over the catalog,
// never a free-add form: offering "Gold Sponsor" twice on one event is meaningless, and a form that
// allows it produces "Such item already exists", which a host cannot act on.
// See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { Pagination, SortItem } from "./commonApiTypes";

// ⚠ The shared partner vocabulary (PARTNER_KIND, partnerKindMeta, …) is NOT re-exported here.
// EventPartnerService already re-exports it from PartnerPackageService, and `utils/api/index.ts` is
// a barrel of `export *` — a second star-export of the same names is ambiguity waiting to happen.
// Import those from PartnerPackageService (or EventPartnerService) directly.

// =============================================================================
// Vocabulary — mirrors src/boundary/partner.ts (PARTNER_PAYMENT_MODE)
// =============================================================================

/**
 * HOW partners pay for ONE offering. Chosen per offering, not per event: a Gold Sponsorship can be
 * charged up front while a Community Partner slot is free, on the same event.
 *
 * ⚠ There is deliberately NO default. The server refuses a create that omits it, because it decides
 * money — see the boundary constant and EventPartnerPackageCore.
 */
export const PARTNER_PAYMENT_MODE = {
  AT_APPLICATION: "at_application",
  ON_APPROVAL: "on_approval",
  OFFLINE: "offline",
} as const;

export const PARTNER_PAYMENT_MODE_VALUES: string[] = Object.values(PARTNER_PAYMENT_MODE);

export const PARTNER_PAYMENT_MODE_LABELS: Record<string, string> = {
  [PARTNER_PAYMENT_MODE.AT_APPLICATION]: "Pay when applying",
  [PARTNER_PAYMENT_MODE.ON_APPROVAL]: "Send a pay link once approved",
  [PARTNER_PAYMENT_MODE.OFFLINE]: "Handle payment yourself",
};

/** The one-line explanation under each choice — a host should not have to guess what they picked. */
export const PARTNER_PAYMENT_MODE_HINTS: Record<string, string> = {
  [PARTNER_PAYMENT_MODE.AT_APPLICATION]: "They pay as part of the form. The slot is only held once the money clears.",
  [PARTNER_PAYMENT_MODE.ON_APPROVAL]: "They apply for free; approving them emails a payment link.",
  [PARTNER_PAYMENT_MODE.OFFLINE]: "Nothing is charged online — record the cash, cheque or transfer yourself.",
};

export const partnerPaymentModeLabel = (mode?: string | null): string =>
  (mode && PARTNER_PAYMENT_MODE_LABELS[mode]) || "—";

// =============================================================================
// GraphQL
// =============================================================================

export const EVENT_PARTNER_PACKAGE_FIELDS = `
  id
  accountId
  eventId
  partnerPackageId
  amount
  serviceFeeAmount
  totalAmount
  feeMode
  currencyCode
  slotsTotal
  slotsTaken
  isOpenForApplications
  paymentMode
  publicSummary
  publicSummaryFormat
  applicationsCloseAt
  orderNo
  status
  createdAt
  updatedAt
  package { id partnerKind name description benefits defaultAmount currencyCode moneyDirection }
`;

const LIST_QUERY = gql`
  query EventPartnerPackageList($filter: EventPartnerPackageFilter, $params: PaginationAndSorting) {
    eventPartnerPackageList(filter: $filter, params: $params) {
      code
      data { ${EVENT_PARTNER_PACKAGE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_QUERY = gql`
  query EventPartnerPackageGet($id: ID) {
    eventPartnerPackageGet(id: $id) {
      code
      data { ${EVENT_PARTNER_PACKAGE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const GET_MANY_QUERY = gql`
  query EventPartnerPackageGetMany($ids: [ID]) {
    eventPartnerPackageGetMany(ids: $ids) {
      code
      data { ${EVENT_PARTNER_PACKAGE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation EventPartnerPackageCreate($params: EventPartnerPackageInput) {
    eventPartnerPackageCreate(params: $params) {
      code
      data { ${EVENT_PARTNER_PACKAGE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const UPDATE_MUTATION = gql`
  mutation EventPartnerPackageUpdate($where: EventPartnerPackageWhereInput, $params: EventPartnerPackageInput) {
    eventPartnerPackageUpdate(where: $where, params: $params) {
      code
      data { ${EVENT_PARTNER_PACKAGE_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const REMOVE_MUTATION = gql`
  mutation EventPartnerPackageRemove($where: EventPartnerPackageWhereInput) {
    eventPartnerPackageRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types
// =============================================================================

/** The catalog package behind an offering, resolved server-side. */
export interface OfferedPackageRef {
  id?: string;
  partnerKind?: string | null;
  name?: string | null;
  description?: string | null;
  benefits?: string[] | null;
  defaultAmount?: number | null;
  currencyCode?: string | null;
  moneyDirection?: string | null;
}

export interface EventPartnerPackage {
  id?: string;
  accountId?: string | null;
  eventId?: string | null;
  partnerPackageId?: string | null;
  /** Per-event price override. Null = fall back to the catalog package's default amount. */
  amount?: number | null;
  /**
   * Our platform service fee on this offering, at today's rate.
   * ⚠ SERVER-DERIVED AND READ-ONLY — never sent back in an input. An offering is a price list, so
   * this reflects today's plan and today's fee_mode; the APPLICATION is where it gets frozen.
   */
  serviceFeeAmount?: number | null;
  /** ⚠ What the SPONSOR will actually be charged. Equals `amount` under absorb, larger otherwise. */
  totalAmount?: number | null;
  /** The event's current fee mode: absorb | service | service_processor. */
  feeMode?: string | null;
  currencyCode?: string | null;
  /** How many are available. Null = unlimited. */
  slotsTotal?: number | null;
  /** How many are taken. Server-maintained under a row lock on approval — never written by the UI. */
  slotsTaken?: number | null;
  /**
   * Whether this offering is taking applications RIGHT NOW.
   * ⚠ NOT the same question as `status`, which is whether it is listed at all — a host wants to show
   * a sold-out tier with applications shut.
   */
  isOpenForApplications?: boolean | null;
  paymentMode?: string | null;
  /** What an APPLICANT reads. The catalog description stays internal to the host. */
  publicSummary?: string | null;
  /** How `publicSummary` is authored: richtext | markdown | html | plain (the policy formats). */
  publicSummaryFormat?: string | null;
  /** Applications shut after this instant. Null = open until the event. */
  applicationsCloseAt?: string | null;
  orderNo?: number | null;
  status?: number;
  createdAt?: string;
  updatedAt?: string;
  package?: OfferedPackageRef | null;
}

export interface EventPartnerPackageInput {
  /** Create-only. */
  eventId?: string | null;
  /** Create-only. */
  partnerPackageId?: string | null;
  amount?: number | null;
  slotsTotal?: number | null;
  /** ⚠ Required on CREATE — the server refuses a create that omits it. */
  isOpenForApplications?: boolean | null;
  /** ⚠ Required on CREATE — the server refuses a create that omits it. */
  paymentMode?: string | null;
  publicSummary?: string | null;
  publicSummaryFormat?: string | null;
  applicationsCloseAt?: string | null;
  status?: number | null;
  orderNo?: number | null;
}

export interface EventPartnerPackageFilter {
  id?: string[] | null;
  eventId?: string[] | null;
  partnerPackageId?: string[] | null;
  status?: number[] | null;
  isOpenForApplications?: boolean | null;
  /** Free text over the OFFERED package's name and description — applied SERVER-side. */
  searchKeyword?: string | null;
}

export interface EventPartnerPackageWhereInput {
  id?: string;
}

// =============================================================================
// Helpers
// =============================================================================

/** What this offering costs: the per-event override, else the catalog default. */
export const offeringAmount = (o: EventPartnerPackage): number | null => {
  if (o.amount !== null && o.amount !== undefined) {
    return Number(o.amount);
  }

  if (o.package?.defaultAmount !== null && o.package?.defaultAmount !== undefined) {
    return Number(o.package.defaultAmount);
  }

  return null;
};

/**
 * What the SPONSOR pays, as opposed to what the host is owed.
 *
 * ⚠ Falls back to the price, never to null — an offering the server could not quote (a slow plan
 * lookup) must read as its face value, not as free.
 */
export const offeringTotalAmount = (o: EventPartnerPackage): number | null =>
  o.totalAmount !== null && o.totalAmount !== undefined ? Number(o.totalAmount) : offeringAmount(o);

/**
 * How much of that is OUR fee being passed on to the sponsor — null when it is not.
 *
 * ⚠ The DIFFERENCE, not `serviceFeeAmount`. Under `absorb` we still charge the host a fee, but the
 * sponsor does not pay it, and showing it beside their price would claim otherwise.
 */
export const offeringFeePassedOn = (o: EventPartnerPackage): number | null => {
  const total = offeringTotalAmount(o);
  const base = offeringAmount(o);

  if (total === null || base === null || total <= base) {
    return null;
  }

  return Math.round((total - base) * 100) / 100;
};

/** True when the price shown came from the catalog rather than this event. */
export const offeringUsesDefaultPrice = (o: EventPartnerPackage): boolean =>
  (o.amount === null || o.amount === undefined) && o.package?.defaultAmount !== null && o.package?.defaultAmount !== undefined;

// The order sponsors see — pure, so `check-event-setup` runs it (see `utils/offeringOrder.ts`).
export { byOfferingDisplayOrder } from '../offeringOrder';

// #A4.7a/b — the EVENT's currency (every offering carries it); the catalog package's own is never a fallback.
export const offeringCurrency = (o: EventPartnerPackage): string | null => o.currencyCode || null;

/**
 * Slots left, or null when the offering is unlimited.
 * ⚠ Never negative: an over-filled offering reads as 0 left, not as "-1 available".
 */
export const offeringSlotsLeft = (o: EventPartnerPackage): number | null => {
  if (o.slotsTotal === null || o.slotsTotal === undefined) {
    return null;
  }

  return Math.max(0, Number(o.slotsTotal) - Number(o.slotsTaken || 0));
};

export const offeringIsFull = (o: EventPartnerPackage): boolean => offeringSlotsLeft(o) === 0;

// =============================================================================
// Service
// =============================================================================

export class EventPartnerPackageService {
  static async list(
    filter?: EventPartnerPackageFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(GET_MANY_QUERY, { ids });
  }

  static async create(params: EventPartnerPackageInput): Promise<OpResult> {
    return ApiService.executeMutation(CREATE_MUTATION, { params });
  }

  static async update(where: EventPartnerPackageWhereInput, params: EventPartnerPackageInput): Promise<OpResult> {
    return ApiService.executeMutation(UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(REMOVE_MUTATION, { where: { id } });
  }
}
